import { useEffect, useMemo, useRef, useState } from "react";
import { FileCheck2, Plus, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMe } from "@/components/app/MeContext";
import { AssetGroupForm } from "@/components/assets/AssetGroupForm";
import { Label } from "@/components/ui/label";
import { Panel } from "@/components/app/common";
import { useFetch } from "@/hooks/useFetch";

export const CONTRACT_TYPES = ["Rental", "Operate", "AMC / CAMC"];
export const LEGACY_CONTRACT_TYPES = ["Rental & Operate", "AMC"];
const FREQ = ["Monthly", "Quarterly", "Half-Yearly", "Yearly", "Custom"];
const SERVICE_TYPES = ["Service visit", "Breakdown", "PM visit", "Ad hoc work", "Spare sale", "Other"];
const YES_NO = [["Yes", "Yes"], ["No", "No"]];
const sel = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm";

function Pick({ label: l, value, onChange, options, testid, empty = "—" }) {
  return (
    <div><Label>{l}</Label>
      <select className={sel} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">{empty}</option>{options.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select></div>
  );
}

export const FAMILY_KEYS = ["contract_type", "contract_start", "contract_end", "financial_year", "billing_frequency", "billing_mode", "default_billing_amount",
  "billing_day", "asset_group_id", "asset_id", "parent_lmo_id", "service_type", "service_date", "service_report_required", "complaint_no", "parts_required"];

const AMC = ["AMC / CAMC", "AMC"];
// Asset settings of the LM Type: { link, after } as "Not Required" | "Required" | "Optional"
export function useAssetRules(prefix, contractType) {
  const { data } = useFetch("/lm-types");
  const t = (data || []).find((x) => x.prefix === prefix);
  if (!t) return { link: "Not Required", after: "Not Required" };
  const amc = AMC.includes(contractType);
  return { link: (amc ? t.amc_asset_link_requirement : t.asset_link_requirement) || "Not Required", after: (amc ? t.amc_asset_creation_after_dispatch : t.asset_creation_after_dispatch) || "Not Required" };
}

function defaultContract(prefix, contractType) {
  if (["Rental", "Operate", "Rental & Operate"].includes(contractType) || (prefix === "LMO" && !contractType)) return "Rental / Operate";
  if (["AMC / CAMC", "AMC"].includes(contractType)) return "AMC";
  return prefix === "LMR" ? "Warranty" : "";
}

function groupEmptyText(customerId, n) {
  if (!customerId) return "Pick the customer first";
  return n ? "— pick —" : "No asset group for this customer yet";
}

// Asset Group picker with "New Asset Group" prefilled from the LM being entered
function GroupPick({ customerId, customer, siteId, f, set }) {
  const { can } = useMe();
  const rule = useAssetRules(f.prefix, f.contract_type);
  const { data, reload } = useFetch(customerId ? `/assets/groups?customer_id=${customerId}` : null);
  const [open, setOpen] = useState(false);
  const opts = (data || []).map((g) => [g.id, `${g.asset_group_code}${g.site_name || g.location_name ? ` · ${g.site_name || g.location_name}` : ""}`]);
  const prefill = useMemo(() => ({ origin_type: f.prefix === "LMO" ? "lmo" : "historical", site_id: siteId || null, lm_ref: f.order_no || "", po_ref: f.customer_po_no || "",
    contract_type: defaultContract(f.prefix, f.contract_type),
    contract_status: "active",
    notes: f.order_no ? "" : `Created while entering a new ${f.prefix}${f.contract_type ? ` (${f.contract_type})` : ""}` }),
  [f.prefix, siteId, f.order_no, f.customer_po_no, f.contract_type]);
  return (
    <div>
      <Pick label={`Asset Group${rule.link === "Required" ? " *" : " (optional)"}`} value={f.asset_group_id} onChange={(v) => set({ asset_group_id: v, asset_id: "" })} options={opts} testid="lm-asset-group" empty={groupEmptyText(customerId, opts.length)} />
      {customer && can("assets.create") && <Button type="button" size="sm" variant="link" className="h-6 px-0 text-xs" onClick={() => setOpen(true)} data-testid="lm-asset-group-new"><Plus className="h-3.5 w-3.5 mr-0.5" />New Asset Group</Button>}
      {open && <AssetGroupForm open={open} onOpenChange={setOpen} initialCustomer={customer} prefill={prefill} onSaved={(g) => { reload(); if (g?.id) set({ asset_group_id: g.id, asset_id: "" }); }} />}
    </div>
  );
}

function AssetPick({ f, set }) {
  const { data } = useFetch(f.asset_group_id ? `/assets/units?group_id=${f.asset_group_id}` : null);
  return <Pick label="Specific asset (optional)" value={f.asset_id} onChange={(v) => set({ asset_id: v })} options={(data || []).map((u) => [u.id, u.asset_code || u.serial_no])} testid="lm-asset" />;
}

function ParentLmoPick({ customerId, f, set }) {
  const { data } = useFetch(customerId ? `/lmo-register?customer_id=${customerId}` : null);
  return <Pick label="Parent contract (LMO, optional)" value={f.parent_lmo_id} onChange={(v) => set({ parent_lmo_id: v })} options={(data || []).map((o) => [o.id, `${o.order_no} · ${o.contract_type || ""}`])} testid="lm-parent-lmo" />;
}

// Indian FY from the contract start (Apr-Mar), kept for the LMO register filter
const fyOf = (d) => {
  if (!d) return "";
  const [y, mo] = d.split("-").map(Number);
  const s = mo >= 4 ? y : y - 1;
  return `${s}-${String((s + 1) % 100).padStart(2, "0")}`;
};

function ContractUpload({ staged, setStaged }) {
  const ref = useRef(null);
  const file = staged?.Contract;
  return (
    <span className="flex items-center gap-2" data-testid="lm-contract-upload">
      {file && <span className="flex items-center gap-1 text-xs text-sky-700" data-testid="lm-contract-file"><FileCheck2 className="h-3.5 w-3.5" />{file.name}
        <button type="button" onClick={() => setStaged((s) => { const n = { ...s }; delete n.Contract; return n; })} data-testid="lm-contract-file-remove"><X className="h-3.5 w-3.5 text-slate-500" /></button></span>}
      <Button size="sm" variant="outline" onClick={() => ref.current?.click()} data-testid="lm-contract-upload-btn"><Upload className="h-4 w-4 mr-1" />{file ? "Replace contract" : "Upload contract"}</Button>
      <input ref={ref} type="file" accept=".pdf,image/*,.doc,.docx" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) setStaged((s) => ({ ...s, Contract: f })); e.target.value = ""; }} data-testid="lm-contract-upload-input" />
    </span>
  );
}

// LMO contract fields / LMS service links, shown only for those families
export function FamilyFields({ f, setF, customerId, customer, siteId, staged, setStaged }) {
  const rule = useAssetRules(f.prefix, f.contract_type);
  const set = (x) => setF((o) => ({ ...o, ...x }));
  const srBlank = f.prefix === "LMS" && !f.service_report_required;
  useEffect(() => { if (srBlank) setF((o) => ({ ...o, service_report_required: "Yes" })); }, [srBlank, setF]);
  const inp = (k, l, type = "text") => <div><Label>{l}</Label><Input type={type} value={f[k] ?? ""} onChange={(e) => set({ [k]: e.target.value, ...(k === "contract_start" ? { financial_year: fyOf(e.target.value) } : {}) })} data-testid={`lm-${k}`} /></div>;
  if (f.prefix === "LMO") return (
    <Panel title="Contract (LMO)" className="mb-4" testid="lm-section-contract" actions={setStaged && <ContractUpload staged={staged} setStaged={setStaged} />}>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        <Pick label="Contract type *" value={f.contract_type} onChange={(v) => set({ contract_type: v })} options={[...CONTRACT_TYPES, ...(LEGACY_CONTRACT_TYPES.includes(f.contract_type) ? [f.contract_type] : [])].map((x) => [x, LEGACY_CONTRACT_TYPES.includes(x) ? `${x} (earlier type)` : x])} testid="lm-contract-type" />
        {inp("contract_start", "Contract start *", "date")}{inp("contract_end", "Contract end *", "date")}
        <Pick label="Billing frequency" value={f.billing_frequency} onChange={(v) => set({ billing_frequency: v })} options={FREQ.map((x) => [x, x])} testid="lm-billing-frequency" />
        <Pick label="Billing mode" value={f.billing_mode} onChange={(v) => set({ billing_mode: v })} options={[["Fixed", "Fixed"], ["Variable", "Variable"]]} testid="lm-billing-mode" />
        {inp("default_billing_amount", "Default billing amount (₹, basic)", "number")}{inp("billing_day", "Preferred billing day (1-31)", "number")}
        <GroupPick customerId={customerId} customer={customer} siteId={siteId} f={f} set={set} />
      </div>
    </Panel>
  );
  if (f.prefix === "LMS") return (
    <Panel title="Service action (LMS) - installed base link" className="mb-4" testid="lm-section-service">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        <Pick label="Service type *" value={f.service_type} onChange={(v) => set({ service_type: v })} options={SERVICE_TYPES.map((x) => [x, x])} testid="lm-service-type" />
        <Pick label="Service report required *" value={f.service_report_required || "Yes"} onChange={(v) => set({ service_report_required: v })} options={YES_NO} testid="lm-service-report-required" />
        {inp("complaint_no", "Complaint number (optional)")}
        <Pick label="Parts required *" value={f.parts_required} onChange={(v) => set({ parts_required: v })} options={YES_NO} testid="lm-parts-required" />
        <p className="col-span-full text-[11px] text-slate-500">Transaction type and product category (above) are also required on an LMS.</p>
        {inp("service_date", "Service date", "date")}
        <GroupPick customerId={customerId} customer={customer} siteId={siteId} f={f} set={set} /><AssetPick f={f} set={set} /><ParentLmoPick customerId={customerId} f={f} set={set} />
      </div>
    </Panel>
  );
  if (rule.link !== "Not Required" || f.asset_group_id) return (
    <Panel title={`Asset link (${rule.link})`} className="mb-4" testid="lm-section-asset-link">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3"><GroupPick customerId={customerId} customer={customer} siteId={siteId} f={f} set={set} /></div>
      <p className="mt-1 text-[11px] text-slate-500">{rule.link === "Required" ? "Needed before submit for approval and before dispatch." : "Optional - link it if the items become assets at a customer site."} Set per LM Type in Admin → Configuration masters.</p>
    </Panel>
  );
  return null;
}
